import { 
  Home, FileText, Users, Settings, Database, Bell, Shield, 
  Leaf, HardHat, Heart, Scale, Download, Upload, Eye, Trash2,
  Edit, Plus, Check, X, AlertCircle, Info, CheckCircle, XCircle,
  Search, Filter, Calendar, Clock, MapPin, Phone, Mail, Globe,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Menu, MoreVertical,
  Image, File, Folder, Archive, FileSpreadsheet, FileImage,
  Save, Copy, Share2, Printer, ExternalLink, RefreshCw, LogOut,
  Sun, Moon, Palette, Circle, Square, Triangle, Star, Hexagon,
  TrendingUp, TrendingDown, BarChart, PieChart, Activity, Award
} from "lucide-react";

export interface ColorItem {
  name: string;
  variable: string;
  hsl: string;
  rgb?: string;
  hex?: string;
  usage: string;
  category: "brand" | "system" | "functional" | "module" | "environmental" | "sidebar";
}

export interface IconItem {
  name: string;
  component: any;
  category: "navigation" | "files" | "status" | "users" | "modules" | "system" | "actions" | "ui";
}

export interface ComponentItem {
  name: string;
  description: string;
  variants: string[];
  example: string;
}

export const brandColors: ColorItem[] = [
  // Brand Colors
  {
    name: "Viable Orange",
    variable: "--viable-orange",
    hsl: "14 100% 60%",
    rgb: "rgb(255, 107, 53)",
    hex: "#FF6B35",
    usage: "Color principal de marca, botones primarios, acentos",
    category: "brand"
  },
  {
    name: "Viable Orange Light",
    variable: "--viable-orange-light",
    hsl: "14 100% 70%",
    rgb: "rgb(255, 138, 92)",
    hex: "#FF8A5C",
    usage: "Variante clara para hover y estados activos",
    category: "brand"
  },
  {
    name: "Viable Green",
    variable: "--viable-green",
    hsl: "142 71% 45%",
    rgb: "rgb(34, 197, 94)",
    hex: "#22C55E",
    usage: "Éxito, estados positivos, módulo ambiental",
    category: "brand"
  },
  {
    name: "Viable Green Light",
    variable: "--viable-green-light",
    hsl: "142 71% 55%",
    rgb: "rgb(74, 222, 128)",
    hex: "#4ADE80",
    usage: "Variante clara para hover",
    category: "brand"
  },
  {
    name: "Viable Blue",
    variable: "--viable-blue",
    hsl: "217 91% 60%",
    rgb: "rgb(59, 130, 246)",
    hex: "#3B82F6",
    usage: "Módulo social, enlaces, información",
    category: "brand"
  },
  {
    name: "Viable Blue Light",
    variable: "--viable-blue-light",
    hsl: "217 91% 70%",
    rgb: "rgb(96, 165, 250)",
    hex: "#60A5FA",
    usage: "Variante clara para hover",
    category: "brand"
  },
  {
    name: "Viable Cyan",
    variable: "--viable-cyan",
    hsl: "180 75% 45%",
    rgb: "rgb(29, 200, 200)",
    hex: "#1DC8C8",
    usage: "Módulo GDB - Geodatabase, cartografía",
    category: "brand"
  },
  {
    name: "Viable Cyan Dark",
    variable: "--viable-cyan-dark",
    hsl: "180 80% 35%",
    rgb: "rgb(18, 161, 161)",
    hex: "#12A1A1",
    usage: "Variante oscura para hover",
    category: "brand"
  },
  {
    name: "Viable Cyan Light",
    variable: "--viable-cyan-light",
    hsl: "180 70% 60%",
    rgb: "rgb(82, 219, 219)",
    hex: "#52DBDB",
    usage: "Variante clara para efectos",
    category: "brand"
  },
  {
    name: "Viable Purple",
    variable: "--viable-purple",
    hsl: "260 66% 67%",
    rgb: "rgb(151, 114, 226)",
    hex: "#9772E2",
    usage: "Módulo Legal - Jurídico y normativo",
    category: "brand"
  },
  {
    name: "Viable Purple Dark",
    variable: "--viable-purple-dark",
    hsl: "260 66% 57%",
    rgb: "rgb(126, 86, 211)",
    hex: "#7E56D3",
    usage: "Variante oscura para hover",
    category: "brand"
  },
  {
    name: "Viable Purple Light",
    variable: "--viable-purple-light",
    hsl: "260 66% 77%",
    rgb: "rgb(176, 142, 241)",
    hex: "#B08EF1",
    usage: "Variante clara para efectos",
    category: "brand"
  },
  {
    name: "Viable Gold",
    variable: "--viable-gold",
    hsl: "36 100% 67%",
    rgb: "rgb(255, 189, 89)",
    hex: "#FFBD59",
    usage: "Módulo Reporte ICA - Reportes integrados",
    category: "module"
  },
  {
    name: "Viable Gold Dark",
    variable: "--viable-gold-dark",
    hsl: "36 100% 57%",
    rgb: "rgb(230, 161, 51)",
    hex: "#E6A133",
    usage: "Variante oscura para hover",
    category: "module"
  },
  {
    name: "Viable Gold Light",
    variable: "--viable-gold-light",
    hsl: "36 100% 77%",
    rgb: "rgb(255, 207, 127)",
    hex: "#FFCF7F",
    usage: "Variante clara para efectos",
    category: "module"
  },

  // Environmental Colors
  {
    name: "Environmental",
    variable: "--environmental",
    hsl: "160 84% 39%",
    rgb: "rgb(16, 185, 129)",
    hex: "#10B981",
    usage: "Módulo ambiental principal",
    category: "environmental"
  },
  {
    name: "Environmental Light",
    variable: "--environmental-light",
    hsl: "160 84% 49%",
    rgb: "rgb(52, 211, 153)",
    hex: "#34D399",
    usage: "Variante clara ambiental",
    category: "environmental"
  },
  {
    name: "Environmental Dark",
    variable: "--environmental-dark",
    hsl: "160 84% 29%",
    rgb: "rgb(6, 148, 102)",
    hex: "#069466",
    usage: "Variante oscura ambiental",
    category: "environmental"
  },

  // System Colors
  {
    name: "Background",
    variable: "--background",
    hsl: "0 0% 100%",
    usage: "Fondo principal de la aplicación",
    category: "system"
  },
  {
    name: "Foreground",
    variable: "--foreground",
    hsl: "222.2 84% 4.9%",
    usage: "Texto principal",
    category: "system"
  },
  {
    name: "Card",
    variable: "--card",
    hsl: "0 0% 100%",
    usage: "Fondo de tarjetas",
    category: "system"
  },
  {
    name: "Card Foreground",
    variable: "--card-foreground",
    hsl: "222.2 84% 4.9%",
    usage: "Texto en tarjetas",
    category: "system"
  },

  // Functional Colors
  {
    name: "Primary",
    variable: "--primary",
    hsl: "14 100% 60%",
    usage: "Botones y elementos principales",
    category: "functional"
  },
  {
    name: "Secondary",
    variable: "--secondary",
    hsl: "210 40% 96.1%",
    usage: "Botones secundarios",
    category: "functional"
  },
  {
    name: "Accent",
    variable: "--accent",
    hsl: "210 40% 96.1%",
    usage: "Elementos de énfasis",
    category: "functional"
  },
  {
    name: "Destructive",
    variable: "--destructive",
    hsl: "0 84.2% 60.2%",
    usage: "Acciones destructivas, errores",
    category: "functional"
  },
  {
    name: "Muted",
    variable: "--muted",
    hsl: "210 40% 96.1%",
    usage: "Fondos sutiles",
    category: "functional"
  },
  {
    name: "Muted Foreground",
    variable: "--muted-foreground",
    hsl: "215.4 16.3% 46.9%",
    usage: "Texto secundario",
    category: "functional"
  },

  // Module Colors
  {
    name: "Social Module",
    variable: "--viable-blue",
    hsl: "217 91% 60%",
    usage: "Módulo de gestión social",
    category: "module"
  },
  {
    name: "Ambiental Module",
    variable: "--viable-green",
    hsl: "142 71% 45%",
    usage: "Módulo de gestión ambiental",
    category: "module"
  },
  {
    name: "SST Module",
    variable: "--yellow-500",
    hsl: "48 96% 53%",
    hex: "#EAB308",
    usage: "Módulo de seguridad y salud en el trabajo",
    category: "module"
  },
  {
    name: "Legal Module",
    variable: "--viable-purple",
    hsl: "260 66% 67%",
    hex: "#9772E2",
    usage: "Módulo legal",
    category: "module"
  },
  {
    name: "GDB Module",
    variable: "--viable-cyan",
    hsl: "180 75% 45%",
    hex: "#1DC8C8",
    usage: "Módulo GDB - Geodatabase",
    category: "module"
  },

  // Sidebar Colors
  {
    name: "Sidebar Background",
    variable: "--sidebar-background",
    hsl: "0 0% 98%",
    usage: "Fondo de la barra lateral",
    category: "sidebar"
  },
  {
    name: "Sidebar Foreground",
    variable: "--sidebar-foreground",
    hsl: "240 5.3% 26.1%",
    usage: "Texto de la barra lateral",
    category: "sidebar"
  },
  {
    name: "Sidebar Accent",
    variable: "--sidebar-accent",
    hsl: "14 100% 60%",
    usage: "Elementos activos en sidebar",
    category: "sidebar"
  },
];

export const iconsList: IconItem[] = [
  // Navigation
  { name: "Home", component: Home, category: "navigation" },
  { name: "Settings", component: Settings, category: "navigation" },
  { name: "ChevronLeft", component: ChevronLeft, category: "navigation" },
  { name: "ChevronRight", component: ChevronRight, category: "navigation" },
  { name: "ChevronUp", component: ChevronUp, category: "navigation" },
  { name: "ChevronDown", component: ChevronDown, category: "navigation" },
  { name: "Menu", component: Menu, category: "navigation" },
  { name: "MoreVertical", component: MoreVertical, category: "navigation" },

  // Files
  { name: "FileText", component: FileText, category: "files" },
  { name: "File", component: File, category: "files" },
  { name: "Folder", component: Folder, category: "files" },
  { name: "Archive", component: Archive, category: "files" },
  { name: "FileSpreadsheet", component: FileSpreadsheet, category: "files" },
  { name: "FileImage", component: FileImage, category: "files" },
  { name: "Image", component: Image, category: "files" },

  // Status
  { name: "Check", component: Check, category: "status" },
  { name: "X", component: X, category: "status" },
  { name: "AlertCircle", component: AlertCircle, category: "status" },
  { name: "Info", component: Info, category: "status" },
  { name: "CheckCircle", component: CheckCircle, category: "status" },
  { name: "XCircle", component: XCircle, category: "status" },
  { name: "TrendingUp", component: TrendingUp, category: "status" },
  { name: "TrendingDown", component: TrendingDown, category: "status" },

  // Users
  { name: "Users", component: Users, category: "users" },
  { name: "Shield", component: Shield, category: "users" },
  { name: "Award", component: Award, category: "users" },

  // Modules
  { name: "Leaf", component: Leaf, category: "modules" },
  { name: "HardHat", component: HardHat, category: "modules" },
  { name: "Heart", component: Heart, category: "modules" },
  { name: "Scale", component: Scale, category: "modules" },

  // System
  { name: "Database", component: Database, category: "system" },
  { name: "Bell", component: Bell, category: "system" },
  { name: "Clock", component: Clock, category: "system" },
  { name: "Calendar", component: Calendar, category: "system" },
  { name: "Activity", component: Activity, category: "system" },
  { name: "BarChart", component: BarChart, category: "system" },
  { name: "PieChart", component: PieChart, category: "system" },

  // Actions
  { name: "Download", component: Download, category: "actions" },
  { name: "Upload", component: Upload, category: "actions" },
  { name: "Edit", component: Edit, category: "actions" },
  { name: "Trash2", component: Trash2, category: "actions" },
  { name: "Plus", component: Plus, category: "actions" },
  { name: "Save", component: Save, category: "actions" },
  { name: "Copy", component: Copy, category: "actions" },
  { name: "Share2", component: Share2, category: "actions" },
  { name: "Printer", component: Printer, category: "actions" },
  { name: "RefreshCw", component: RefreshCw, category: "actions" },
  { name: "LogOut", component: LogOut, category: "actions" },

  // UI
  { name: "Eye", component: Eye, category: "ui" },
  { name: "Search", component: Search, category: "ui" },
  { name: "Filter", component: Filter, category: "ui" },
  { name: "Sun", component: Sun, category: "ui" },
  { name: "Moon", component: Moon, category: "ui" },
  { name: "Palette", component: Palette, category: "ui" },
  { name: "MapPin", component: MapPin, category: "ui" },
  { name: "Phone", component: Phone, category: "ui" },
  { name: "Mail", component: Mail, category: "ui" },
  { name: "Globe", component: Globe, category: "ui" },
  { name: "ExternalLink", component: ExternalLink, category: "ui" },
  { name: "Circle", component: Circle, category: "ui" },
  { name: "Square", component: Square, category: "ui" },
  { name: "Triangle", component: Triangle, category: "ui" },
  { name: "Star", component: Star, category: "ui" },
  { name: "Hexagon", component: Hexagon, category: "ui" },
];

export const componentExamples: ComponentItem[] = [
  {
    name: "Button",
    description: "Botones interactivos con múltiples variantes",
    variants: ["default", "secondary", "outline", "ghost", "destructive", "link"],
    example: '<Button variant="default">Click me</Button>'
  },
  {
    name: "Card",
    description: "Contenedores para agrupar contenido relacionado",
    variants: ["default", "with-header", "with-footer", "interactive"],
    example: '<Card><CardHeader><CardTitle>Title</CardTitle></CardHeader><CardContent>Content</CardContent></Card>'
  },
  {
    name: "Badge",
    description: "Etiquetas para mostrar estados o categorías",
    variants: ["default", "secondary", "destructive", "outline"],
    example: '<Badge>New</Badge>'
  },
  {
    name: "Alert",
    description: "Mensajes de notificación para el usuario",
    variants: ["default", "destructive"],
    example: '<Alert><AlertCircle /><AlertTitle>Heads up!</AlertTitle><AlertDescription>Message</AlertDescription></Alert>'
  },
  {
    name: "Input",
    description: "Campos de entrada de texto",
    variants: ["text", "email", "password", "number", "date"],
    example: '<Input type="text" placeholder="Enter text" />'
  },
  {
    name: "Select",
    description: "Selectores desplegables",
    variants: ["single", "multiple", "searchable"],
    example: '<Select><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>...</SelectContent></Select>'
  },
  {
    name: "Dialog",
    description: "Ventanas modales",
    variants: ["center", "full-screen", "drawer"],
    example: '<Dialog><DialogTrigger>Open</DialogTrigger><DialogContent>...</DialogContent></Dialog>'
  },
  {
    name: "Tabs",
    description: "Navegación por pestañas",
    variants: ["default", "pills", "underline"],
    example: '<Tabs><TabsList><TabsTrigger>Tab 1</TabsTrigger></TabsList><TabsContent>...</TabsContent></Tabs>'
  },
];

export const typographyScale = [
  { name: "h1", class: "text-4xl font-bold", size: "36px", usage: "Títulos principales de página" },
  { name: "h2", class: "text-3xl font-bold", size: "30px", usage: "Títulos de sección" },
  { name: "h3", class: "text-2xl font-semibold", size: "24px", usage: "Subtítulos" },
  { name: "h4", class: "text-xl font-semibold", size: "20px", usage: "Títulos de tarjetas" },
  { name: "body-lg", class: "text-lg", size: "18px", usage: "Texto grande" },
  { name: "body", class: "text-base", size: "16px", usage: "Texto estándar" },
  { name: "body-sm", class: "text-sm", size: "14px", usage: "Texto pequeño" },
  { name: "caption", class: "text-xs", size: "12px", usage: "Texto de apoyo, metadatos" },
];

export const spacingScale = [
  { value: "0", px: "0px", usage: "Sin espaciado" },
  { value: "1", px: "4px", usage: "Espaciado mínimo" },
  { value: "2", px: "8px", usage: "Espaciado muy pequeño" },
  { value: "3", px: "12px", usage: "Espaciado pequeño" },
  { value: "4", px: "16px", usage: "Espaciado estándar" },
  { value: "6", px: "24px", usage: "Espaciado medio" },
  { value: "8", px: "32px", usage: "Espaciado grande" },
  { value: "12", px: "48px", usage: "Espaciado muy grande" },
  { value: "16", px: "64px", usage: "Espaciado extra grande" },
  { value: "20", px: "80px", usage: "Espaciado máximo" },
];

export const animations = [
  { name: "fade-in", duration: "0.3s", usage: "Aparición suave de elementos" },
  { name: "fade-out", duration: "0.3s", usage: "Desaparición suave de elementos" },
  { name: "scale-in", duration: "0.2s", usage: "Zoom in de elementos" },
  { name: "scale-out", duration: "0.2s", usage: "Zoom out de elementos" },
  { name: "slide-in-right", duration: "0.3s", usage: "Entrada desde la derecha" },
  { name: "slide-out-right", duration: "0.3s", usage: "Salida hacia la derecha" },
  { name: "accordion-down", duration: "0.2s", usage: "Expansión de acordeones" },
  { name: "accordion-up", duration: "0.2s", usage: "Colapso de acordeones" },
];
